import { AccessLevel } from "@/api/williams/users";
import { Associations } from "./Associations";
import { Certifications } from "./../components/Certifications";
import { ConfirmationDialog } from "@/components/dialogs";
import { Expirations } from "./../components/Expirations";
import { faFilePdf, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faPenToSquare, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { getFullPath, routes } from "../../routes";
import { IconButton } from "../../components";
import { Metrics } from "./Metrics";
import { ProcessStatus } from "./ProcessStatus";
import { Projects } from "../../projects";
import { ScopesOfWork } from "../../scopeOfWork";
import { StatusHistory } from "./StatusHistory";
import { TestEvents } from "../../testEvents";
import { useDeleteWelder, usePrintWelder } from "./../hooks";
import { useModalDialog } from "@/hooks";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../users/hooks";
import { Welder } from "@/api/williams/welders";
import { WelderDetails } from "./WelderDetails";
import { WelderUpdates } from "./Updates";

export function Dashboard({ welder }: { welder: Welder }) {
  const dialog = useModalDialog();
  const print = usePrintWelder();
  const deleteWelder = useDeleteWelder();
  const permissions = usePermissions();
  const navigate = useNavigate();

  function handleEditWelder() {
    navigate(getFullPath(routes.welder.edit(welder.welderId)));
  }

  function handleAddRenewal() {
    navigate(getFullPath(routes.welder.renewal), {
      state: {
        welders: [welder],
      },
    });
  }

  function handleDeleteWelder() {
    dialog.show(ConfirmationDialog, {
      caption: "Delete Welder",
      message: "Are you sure you want to delete this welder?",
      onAccept: () => {
        deleteWelder.mutate(welder, {
          onSuccess: () => navigate(getFullPath(routes.welder.list())),
        });
      },
    });
  }

  return (
    <>
      <div className="d-md-flex justify-content-between mb-2">
        <h3>Welder Dashboard</h3>
        {permissions.data && permissions.data.accessLevel !== AccessLevel.GeneralUser && (
          <div className="d-flex gap-2 my-auto">
            {permissions.data.accessLevel >= AccessLevel.AIWeldingSME && (
              <IconButton icon={faPenToSquare} title="Edit" onClick={handleEditWelder} />
            )}
            <IconButton icon={faFilePdf} title="Download PDF" onClick={() => print.showDialog(welder.welderId)} />
            <IconButton icon={faUserPlus} title="Add Renewal" onClick={handleAddRenewal} />
            {permissions.data.accessLevel >= AccessLevel.AIWeldingSME && (
              <IconButton icon={faTrashAlt} title="Delete" onClick={handleDeleteWelder} />
            )}
          </div>
        )}
      </div>

      <div className="card mb-3">
        <div className="card-header">Welder Details</div>
        <div className="card-body" style={{ maxWidth: 1000 }}>
          <WelderDetails welder={welder} />
        </div>
      </div>

      <div>
        <ul className="mb-5">
          <li className="mb-3">
            <Metrics welderId={welder.welderId} />
          </li>
          <li className="mb-3">
            <Associations welderId={welder.welderId} />
          </li>
          <li className="mb-3">
            <Projects filters={{ welderId: welder.welderId }} />
          </li>
          <li className="mb-3">
            <ScopesOfWork filters={{ welderId: welder.welderId }} />
          </li>
          <li className="mb-3">
            <TestEvents filters={{ welderId: welder.welderId }} />
          </li>
          <li className="mb-3">
            <Certifications filters={{ welderId: welder.welderId }} />
          </li>
          <li className="mb-3">
            <WelderUpdates welderId={welder.welderId} />
          </li>
          <li className="mb-3">
            <Expirations filters={{ welderId: welder.welderId }} />
          </li>
          <li className="mb-3">
            <ProcessStatus welderId={welder.welderId} />
          </li>
          <li className="mb-3">
            <StatusHistory welderId={welder.welderId} />
          </li>
        </ul>
      </div>
    </>
  );
}
